import type { SessionService } from '@efdigital/ll-addon-auth';
import type { UserGroup } from '@efdigital/ll-addon-data';
import {
  buildGetUsersByUserId,
  buildSearchUsersByEmail,
  isValidEmail,
} from '@efdigital/ll-admin-v2/data/builders/system/users';
import { deepRollback } from '@efdigital/ll-admin-v2/data/deep-rollback';
import { isDummyUser } from '@efdigital/ll-admin-v2/utils/user-groups';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

import { EditRecord, type EditRecordSignature } from '../edit-record.gts';
import { Validation } from '../validation.gts';

type UserInfo = {
  id: string;
  title: string;
  lastLogin?: string;
  created?: string;
};

export const pendingPrefix = '--pending||';
export const pendingRegex = /^--pending\|\|/;

export const toPending = (email: string) =>
  `${pendingPrefix}${email.toLowerCase()}`;
export const fromPending = (pendingId: string) =>
  pendingId.replace(pendingRegex, '');
export const isPending = (userId: string) => pendingRegex.test(userId);

export class EditUserGroupUsers extends Component<
  EditRecordSignature<
    UserGroup,
    {
      editLabel: string;
      legend: string;
      helpText?: string;
      save: (item: UserGroup) => Promise<void>;
    }
  >
> {
  @service declare session: SessionService;
  @service declare store: Store;

  @tracked addedUsers: UserInfo[] = [];
  @tracked removedUsers: UserInfo[] = [];

  validate = () => {
    if (!this.mergedUserIds.length) {
      return Validation.fromFailures([
        {
          message:
            'There must be at least one user in the group (pending or registered)',
        },
      ]);
    } else {
      return Validation.success();
    }
  };

  getUsers = async () => {
    const request = await buildGetUsersByUserId(
      {
        includeDeleted: true,
        id: (this.args.item.userIds || []).filter((id) => !isPending(id)) || [],
      },
      this.session
    );

    const response = await fetch(request);
    let data: UserInfo[] = [];
    if (response.ok) {
      data = (await response.json()) as UserInfo[];
    } else {
      console.error('Failed to get users: ', response.statusText);
    }

    return (this.args.item.userIds || [])
      .filter((userId) => !isDummyUser(userId))
      .map((userId) =>
        isPending(userId)
          ? {
              id: userId,
              title: fromPending(userId),
              lastLogin: '',
              created: '',
            }
          : data.find((user) => user.id === userId) || {
              id: userId,
              lastLogin: '',
              created: '',
              title: `Unknown user ID: ${userId}`,
            }
      );
  };

  sortUsers = (users: UserInfo[]) => {
    return users.sort((a, b) => {
      return a.title.localeCompare(b.title);
    });
  };

  resolveUsers = async () => {
    if (!this.searchValue) {
      return;
    }

    const emails = this.searchValue
      .split(/\s+/)
      .filter((f) => f)
      .filter((f) => isValidEmail(f));

    if (!emails.length) {
      return;
    }

    const request = await buildSearchUsersByEmail(
      {
        emails,
      },
      this.session
    );

    const response = await fetch(request);
    if (!response.ok) {
      throw new Error(`Failed to find users: ${response.statusText}`);
    }
    const searchResults = (await response.json()) as UserInfo[];

    const usersToAdd = emails.map((text) => {
      const textLower = text.toLowerCase();
      const existing = searchResults.find(
        (i) => i.title.toLowerCase() === textLower
      );
      if (existing) {
        return existing;
      }

      const placeholderUser: UserInfo = {
        id: toPending(textLower),
        title: textLower,
        lastLogin: '',
      };

      return placeholderUser;
    });

    this.addedUsers = [
      ...this.addedUsers,
      ...usersToAdd.filter(
        (u) =>
          !this.addedUsers.find((a) => a.id === u.id) &&
          !(this.args.item.userIds || []).find((id) => id === u.id)
      ),
    ];

    this.removedUsers = this.removedUsers.filter(
      (u) => !usersToAdd.find((a) => a.id === u.id)
    );

    this.searchValue = '';
  };

  removeUser = (user: UserInfo) => {
    if (this.addedUsers.find((u) => u.id === user.id)) {
      this.addedUsers = this.addedUsers.filter((u) => u.id !== user.id);
    }
    if ((this.args.item.userIds || []).find((id) => id === user.id)) {
      this.removedUsers = Array.from(new Set([...this.removedUsers, user]));
    }
  };

  restoreUser = (user: UserInfo) => {
    if (this.removedUsers.find((u) => u.id === user.id)) {
      this.removedUsers = this.removedUsers.filter((u) => u.id !== user.id);
    }
  };

  isRemoved = (user: UserInfo) => {
    if (isDummyUser(user.id)) {
      return true;
    }
    return this.removedUsers.find((u) => u.id === user.id);
  };

  @tracked searchValue = '';

  updateSearchValue = (event: Event) => {
    const textarea = event.target as HTMLTextAreaElement;
    this.searchValue = textarea.value;
  };

  saveUsers = () => {
    this.args.item.userIds = this.mergedUserIds;
    this.removedUsers = [];
    this.addedUsers = [];
    return this.args.save(this.args.item);
  };

  revertUsers = () => {
    this.addedUsers = [];
    this.removedUsers = [];
    return deepRollback(this.args.item, this.store, {
      preventNewRecordUnload: true,
    });
  };

  get mergedUserIds() {
    return [
      ...(this.args.item.userIds || []).filter(
        (id) => isDummyUser(id) || !this.removedUsers.find((u) => u.id === id)
      ),
      ...this.addedUsers.map((u) => u.id),
    ];
  }

  <template>
    <EditRecord
      @item={{@item}}
      @context={{@context}}
      @buttonSize="btn-md"
      @editLabel={{@editLabel}}
      @validate={{this.validate}}
      @save={{this.saveUsers}}
      @revert={{this.revertUsers}}
    >
      <:form as |uniqueId isReadOnly|>
        <Await @promise={{(this.getUsers)}}>
          <:success as |users|>
            <fieldset disabled={{isReadOnly}} class="mb-4">
              <legend class="gud-eyebrow">{{@legend}}</legend>

              {{#if @helpText}}
                <p>{{@helpText}}</p>
              {{/if}}

              {{#if isReadOnly}}
                {{#unless users.length}}
                  <p>No users have been assigned.</p>
                {{/unless}}
              {{/if}}

              <div class="list-group mb-3">
                {{#each (this.sortUsers users) as |userInfo|}}
                  {{#let (this.isRemoved userInfo) as |isRemoved|}}
                    <div
                      class="list-group-item
                        {{if isRemoved 'list-group-item-danger'}}"
                    >
                      <div
                        class="d-flex justify-content-start align-items-top gap-3"
                      >
                        <div class="flex-shrink-0 flex-grow-0">
                          {{#if isRemoved}}
                            <iconify-icon
                              inline
                              icon="ph:user-fill"
                              class="text-danger"
                            />
                          {{else}}
                            <iconify-icon inline icon="ph:user-bold" />
                          {{/if}}
                        </div>
                        <div
                          class="flex-grow-1
                            {{if isRemoved 'text-decoration-line-through'}}"
                        >
                          {{userInfo.title}}
                          {{#if (isPending userInfo.id)}}
                            <span class="badge text-bg-dark">Pending</span>
                          {{/if}}
                        </div>
                        {{#unless isReadOnly}}
                          {{#if isRemoved}}
                            <button
                              type="button"
                              class="btn btn-outline-danger btn-sm m-0 py-0"
                              {{on "click" (fn this.restoreUser userInfo)}}
                            >
                              <iconify-icon inline icon="ph:plus-bold" />
                              <span class="visually-hidden">Restore user</span>
                            </button>
                          {{else}}
                            <button
                              type="button"
                              class="btn btn-outline-danger btn-sm m-0 py-0"
                              {{on "click" (fn this.removeUser userInfo)}}
                            >
                              <iconify-icon inline icon="ph:minus-bold" />
                              <span class="visually-hidden">Remove user</span>
                            </button>
                          {{/if}}
                        {{/unless}}
                      </div>
                    </div>
                  {{/let}}
                {{/each}}
                {{#each (this.sortUsers this.addedUsers) as |userInfo|}}
                  <div class="list-group-item list-group-item-success">
                    <div
                      class="d-flex justify-content-start align-items-top gap-3"
                    >
                      <div class="flex-shrink-0 flex-grow-0">
                        <iconify-icon
                          inline
                          icon="ph:user-fill"
                          class="text-success"
                        />
                      </div>
                      <div class="flex-grow-1">
                        {{userInfo.title}}
                        {{#if (isPending userInfo.id)}}
                          <span class="badge text-bg-dark">Pending</span>
                        {{/if}}
                      </div>
                      <button
                        type="button"
                        class="btn btn-outline-danger btn-sm m-0 py-0"
                        {{on "click" (fn this.removeUser userInfo)}}
                      >
                        <iconify-icon inline icon="ph:x-bold" />
                        <span class="visually-hidden">Remove user</span>
                      </button>
                    </div>
                  </div>
                {{/each}}
              </div>

              {{#unless isReadOnly}}
                <div class="card bg-secondary-subtle">
                  <div class="card-body">
                    <div class="mb-3">
                      <label
                        for="{{uniqueId}}-addUserInput"
                        class="form-label mb-1"
                      >Add users by email address</label>
                      <p class="form-text">
                        Separate multiple emails with spaces or new lines.
                      </p>
                      <textarea
                        class="form-control"
                        id="{{uniqueId}}-addUserInput"
                        placeholder="Enter user emails"
                        rows="4"
                        value={{this.searchValue}}
                        {{on "change" this.updateSearchValue}}
                      />
                    </div>
                    <div class="text-center">
                      <button
                        type="button"
                        class="btn btn-secondary btn-md"
                        {{on "click" this.resolveUsers}}
                      >
                        Add Users
                      </button>
                    </div>
                  </div>
                </div>
              {{/unless}}
            </fieldset>
          </:success>
        </Await>
      </:form>
    </EditRecord>
  </template>
}
